// eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-imports-ts
import { FunctionalComponent, h } from '@stencil/core';

import BonusCode from './BonusCode';
import NoFollowLink from './NoFollowLink';
import { FormattedPromotion } from '../../types';
import showTerms from '../../utils/terms';

interface PromotionCardProps {
    promotion: FormattedPromotion | null;
    logPromotionClick: () => void;
    handleBonusCodeClick?: () => void;
    bonusCodeWasClicked?: boolean;
    useBoxShadow?: boolean;
    promoValidDate?: string;
    terms?: 'geo' | 'affiliate';
    reveal?: string;
    revealText?: string;
}

const BonusPromotionCard: FunctionalComponent<PromotionCardProps> = ({
    promotion,
    logPromotionClick,
    handleBonusCodeClick,
    bonusCodeWasClicked = false,
    useBoxShadow = false,
    promoValidDate = '',
    terms = 'geo',
    reveal = 'false',
    revealText = 'Reveal Code',
}) => {
    const termsText = promotion ? showTerms(promotion, terms) : '';
    const arrowImageSrc = 'https://bam-static.actionnetwork.com/assets/arrow_curved.png';

    if (reveal === 'animated' && revealText === 'Reveal Code') {
        revealText = 'Reveal Bonus Code'; // Default value for revealText is different on animated
    }

    const handleCodeClick = () => {
        logPromotionClick();
        if (handleBonusCodeClick) {
            handleBonusCodeClick();
        }
    };

    const propMarfeelConversion = promotion?.link && promotion?.link.includes('switchboard') ? 'switchboard-click' : '';

    return (
        <div class="item font">
            <div class="item__color-border" style={{ backgroundColor: `#${promotion.logoColor}` }}>
                <div class="item__date">
                    <p>Updated on {promoValidDate}</p>
                </div>
                <div class="item__content">
                    <div class="item__promo-container">
                        {promotion ? (
                            <lazy-load-logo
                                class="item__logo"
                                logo={promotion.logo}
                                promoId={promotion.id}
                                logoColor={promotion.logoColor}
                            />
                        ) : (
                            <div class="skeleton-item__logo"></div>
                        )}
                        <div class={`${promotion ? '' : 'skeleton-'}item__description`}>
                            <div class="item__book-name">{promotion?.bookName}</div>
                            <div class="item__cta">{promotion?.cta}</div>
                        </div>
                    </div>
                    <div>
                        {reveal === 'false' && (
                            <NoFollowLink
                                class={useBoxShadow ? `item item__shadow font` : `item font`}
                                onClick={logPromotionClick}
                                href={promotion?.link}
                            >
                                <div class="buttons">
                                    {handleBonusCodeClick && (
                                        <div class="bonus-striped">
                                            <div class="bonus-code-label">Bonus Code</div>
                                            <BonusCode
                                                bonusCode={promotion?.bonusCode}
                                                bonusCodeWasClicked={bonusCodeWasClicked}
                                                handleClick={handleBonusCodeClick}
                                                includeDescriptor={false}
                                            />
                                        </div>
                                    )}
                                    <div class="button" data-mrf-conversion={propMarfeelConversion}>
                                        <span class="button-font">{promotion?.buttonText || 'CLAIM NOW'}</span>
                                    </div>
                                </div>
                            </NoFollowLink>
                        )}
                        {reveal === 'true' && (
                            <NoFollowLink
                                class={useBoxShadow ? `item item__shadow font` : `item font`}
                                onClick={handleCodeClick}
                                href={promotion?.link}
                                target="_blank"
                            >
                                <div>
                                    {!bonusCodeWasClicked && (
                                        <div class="buttons-vert">
                                            <div class="bonus-striped">
                                                <div></div>
                                                <BonusCode
                                                    bonusCode={promotion?.bonusCode}
                                                    bonusCodeWasClicked={bonusCodeWasClicked}
                                                    handleClick={handleBonusCodeClick}
                                                    includeDescriptor={false}
                                                />
                                                <span class="bonus-cover" data-mrf-conversion={propMarfeelConversion}>
                                                    {revealText}
                                                </span>
                                                <span class="bonus-fold"></span>
                                                <span class="bonus-lowerfold"></span>
                                            </div>
                                            <div class="bonus-arrow">
                                                <img src={arrowImageSrc} height="28" width="17" alt="Bonus Arrow" />
                                                <span class="bonus-arrow__claim">claim your code right here</span>
                                            </div>
                                        </div>
                                    )}
                                    {bonusCodeWasClicked && (
                                        <div class="buttons-vert">
                                            <div class="bonus-striped-show">
                                                <div>{promotion?.bonusCode || 'No Code Needed'}</div>
                                                <div>Continue to {promotion?.bookName}</div>
                                            </div>
                                            <div class="bonus-arrow">
                                                <img src={arrowImageSrc} height="28" width="17" alt="Bonus Arrow" />
                                                <span class="bonus-arrow__claim">claim your code right here</span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </NoFollowLink>
                        )}
                        {reveal === 'animated' && (
                            <div>
                                {!bonusCodeWasClicked && (
                                    <NoFollowLink
                                        class={useBoxShadow ? `item item__shadow font` : `item font`}
                                        onClick={logPromotionClick}
                                        href={promotion?.link}
                                        target="_blank"
                                    >
                                        <div style={{ position: 'relative', marginBottom: '0.5rem' }}>
                                            <div style={{ position: 'relative', width: '100%' }}>
                                                <input
                                                    type="text"
                                                    value={`${promotion?.bonusCode || 'NO CODE NEEDED'}`}
                                                    readonly="readonly"
                                                    class="bonus-input"
                                                />
                                                <button
                                                    class="anim_counter_btn anim-btn"
                                                    onClick={handleBonusCodeClick}
                                                    data-mrf-conversion={propMarfeelConversion}
                                                >
                                                    <span class="anim_bonus-cover">{revealText}</span>
                                                </button>
                                                <div class="button__corner"></div>
                                            </div>
                                        </div>
                                        <div class="bonus-arrow">
                                            <img src={arrowImageSrc} height="28" width="17" alt="Bonus Arrow" />
                                            <span class="bonus-arrow__claim">claim your code right here</span>
                                        </div>
                                    </NoFollowLink>
                                )}
                                {bonusCodeWasClicked && (
                                    <div class="item font">
                                        <div class="buttons-grid">
                                            <div class="cell">
                                                <div class="bonus-input-show">
                                                    {promotion?.bonusCode || 'NO CODE NEEDED'}
                                                </div>
                                            </div>
                                            <div class="button">
                                                <NoFollowLink
                                                    class={useBoxShadow ? `item item__shadow font` : `item font`}
                                                    onClick={logPromotionClick}
                                                    href={promotion?.link}
                                                >
                                                    <span class="button-font">Continue to {promotion?.bookName}</span>
                                                </NoFollowLink>
                                            </div>
                                        </div>
                                        <div class="bonus-arrow" style={{ marginTop: '0.5rem' }}>
                                            <img src={arrowImageSrc} height="28" width="17" alt="Bonus Arrow" />
                                            <span class="bonus-arrow__claim">claim your code right here</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    <div class="item__terms">{termsText}</div>
                </div>
            </div>
        </div>
    );
};

export default BonusPromotionCard;
